/**
 * Accept Invite Page Component (TASK-216)
 * Page where invited users land to set their password.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, KeyRound } from 'lucide-react';
import apiClient from '@/services/api/client';

type FieldErrors = Partial<
  Record<'firstName' | 'lastName' | 'password' | 'passwordConfirm', string>
>;

export function AcceptInvitePage() {
  const navigate = useNavigate();
  const { uid, token } = useParams<{ uid: string; token: string }>();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation (TASK-334): validate ALL fields in a single
    // pass (no early return) so every invalid field gets its own inline
    // message. The required check always wins over the length/mismatch
    // checks — those only run when the field is already non-empty.
    const errors: FieldErrors = {};

    if (!firstName.trim()) {
      errors.firstName = 'Обязательное поле';
    }

    if (!lastName.trim()) {
      errors.lastName = 'Обязательное поле';
    }

    if (!password) {
      errors.password = 'Обязательное поле';
    } else if (password.length < 8) {
      errors.password = 'Минимум 8 символов';
    }

    if (!passwordConfirm) {
      errors.passwordConfirm = 'Обязательное поле';
    } else if (password && password !== passwordConfirm) {
      errors.passwordConfirm = 'Пароли не совпадают';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setIsLoading(true);

    try {
      await apiClient.post('/api/v1/auth/accept-invite/', {
        uid,
        token,
        password,
        password_confirm: passwordConfirm,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      navigate('/login');
    } catch (err: unknown) {
      const error = err as {
        response?: {
          status?: number;
          data?: Record<string, string | string[]>;
        };
      };
      const data = error.response?.data;

      if (data) {
        const detail = (data.detail || data.error || '') as string;
        const isInvalidToken =
          typeof detail === 'string' &&
          (detail.toLowerCase().includes('invalid token') ||
            detail.includes('Недействительн'));
        if (isInvalidToken) {
          setError('Недействительная ссылка');
        } else {
          const messages = Object.values(data)
            .flat()
            .join(', ');
          setError(messages || 'Ошибка. Попробуйте позже.');
        }
      } else {
        setError('Ошибка. Попробуйте позже.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-blue-600">
            SMETA AI
          </h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
            Установить пароль
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Создайте пароль для вашего аккаунта
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="first_name"
                className="text-sm font-medium leading-none"
              >
                Имя
              </label>
              <Input
                id="first_name"
                name="first_name"
                type="text"
                autoComplete="given-name"
                required
                maxLength={150}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1"
                aria-invalid={fieldErrors.firstName ? 'true' : undefined}
                aria-describedby={
                  fieldErrors.firstName ? 'first_name-error' : undefined
                }
              />
              {fieldErrors.firstName && (
                <p id="first_name-error" className="mt-1 text-sm text-red-600">
                  {fieldErrors.firstName}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="last_name"
                className="text-sm font-medium leading-none"
              >
                Фамилия
              </label>
              <Input
                id="last_name"
                name="last_name"
                type="text"
                autoComplete="family-name"
                required
                maxLength={150}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1"
                aria-invalid={fieldErrors.lastName ? 'true' : undefined}
                aria-describedby={
                  fieldErrors.lastName ? 'last_name-error' : undefined
                }
              />
              {fieldErrors.lastName && (
                <p id="last_name-error" className="mt-1 text-sm text-red-600">
                  {fieldErrors.lastName}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="text-sm font-medium leading-none"
              >
                Пароль
              </label>
              <PasswordInput
                id="password"
                name="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                aria-invalid={fieldErrors.password ? 'true' : undefined}
                aria-describedby={
                  fieldErrors.password ? 'password-error' : undefined
                }
              />
              {fieldErrors.password && (
                <p id="password-error" className="mt-1 text-sm text-red-600">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password_confirm"
                className="text-sm font-medium leading-none"
              >
                Подтвердите пароль
              </label>
              <PasswordInput
                id="password_confirm"
                name="password_confirm"
                autoComplete="new-password"
                required
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="mt-1"
                aria-invalid={fieldErrors.passwordConfirm ? 'true' : undefined}
                aria-describedby={
                  fieldErrors.passwordConfirm ? 'password_confirm-error' : undefined
                }
              />
              {fieldErrors.passwordConfirm && (
                <p
                  id="password_confirm-error"
                  className="mt-1 text-sm text-red-600"
                >
                  {fieldErrors.passwordConfirm}
                </p>
              )}
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Установка пароля...
              </>
            ) : (
              <>
                <KeyRound className="mr-2 h-4 w-4" />
                Установить пароль
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default AcceptInvitePage;
